import React, { ForwardedRef } from 'react';
import clsx from 'clsx';

import styles from '@danske/sapphire-css/components/table/table.module.css';
import { DOMAttributes } from '@react-types/shared';
import { mergeProps } from 'react-aria/mergeProps';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export interface TableBodyProps
  extends Omit<
      DOMAttributes<HTMLTableSectionElement>,
      'className' | 'style' | 'dangerouslySetInnerHTML'
    >,
    SapphireStyleProps {
  /**
   * Note: Currently, we don't officially support non-table elements.
   *
   * @default 'tbody'
   */
  elementType?: 'tbody';
  children?: React.ReactNode;
}

/**
 * Corresponding to `tbody` element in native html. Should be rendered inside
 * {@Link Table}. Accepts a set of {@Link Table.Row} as children
 */

export const TableBody = React.forwardRef(function TableBody(
  { children, elementType, ...props }: TableBodyProps,
  ref: ForwardedRef<HTMLTableSectionElement>
) {
  const ElementType = elementType || 'tbody';
  const role = props.role || ElementType === 'tbody' ? undefined : 'rowgroup';

  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  return (
    <ElementType
      ref={ref}
      {...mergeProps(filteredProps, { role })}
      className={clsx(styles['sapphire-table__body'], styleProps.className)}
      style={styleProps.style}
    >
      {children}
    </ElementType>
  );
});
